import { ReactNode, createContext, useCallback, useEffect, useRef, useState } from "react";
import jwt_decode from "jwt-decode";
import AuthContextValue, { AuthStatus } from "./models/AuthContextValue";
import { dropJWT, getJWT, restoreJWT, writeLog } from "./helpers/helpers";
import { setActiveAccessToken } from "./services/Google/accessToken";

const CLIENT_ID = "443420267186-4loku40pnc4c89r04qnj5e4aa6aurkt9.apps.googleusercontent.com";
const SCOPES = [
  "https://www.googleapis.com/auth/documents",
  "https://www.googleapis.com/auth/drive",
  "https://www.googleapis.com/auth/spreadsheets",
  "https://www.googleapis.com/auth/gmail.send",
] as const;
const ACCESS_TOKEN_KEY = "pbeej.googleAccessToken";
const ACCESS_TOKEN_EXPIRY_KEY = "pbeej.googleAccessTokenExpiresAt";
const ACCESS_TOKEN_ACCOUNT_KEY = "pbeej.googleAccessTokenAccount";
const EXPIRY_BUFFER_MS = 60_000;
const GOOGLE_SCRIPT_TIMEOUT_MS = 15_000;

type Session = {
  hasJwt: boolean;
  accessToken?: string;
  expiresAt?: number;
  accountId?: string;
  email?: string;
};

type DriveRequestState = "idle" | "pending" | "ready" | "committing" | "failed";

function logAuthError(message: string, ...details: unknown[]) {
  console.error(message, ...details);

  void writeLog({
    source: "AuthContext",
    level: "error",
    message,
    details: details.map((detail) =>
      detail instanceof Error
        ? { name: detail.name, message: detail.message, stack: detail.stack }
        : detail,
    ),
  }).catch(() => undefined);
}

function readStoredAccessToken(): Pick<Session, "accessToken" | "expiresAt" | "accountId"> {
  try {
    const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    const accountId = sessionStorage.getItem(ACCESS_TOKEN_ACCOUNT_KEY);
    const expiresAt = Number(sessionStorage.getItem(ACCESS_TOKEN_EXPIRY_KEY));
    if (!token || !accountId || !Number.isFinite(expiresAt) || Date.now() >= expiresAt - EXPIRY_BUFFER_MS) {
      clearStoredAccessToken();
      return {};
    }
    return { accessToken: token, expiresAt, accountId };
  } catch {
    return {};
  }
}

function storeAccessToken(token: string, expiresInSeconds: number, accountId: string) {
  const expiresAt = Date.now() + expiresInSeconds * 1000;
  try {
    sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
    sessionStorage.setItem(ACCESS_TOKEN_EXPIRY_KEY, String(expiresAt));
    sessionStorage.setItem(ACCESS_TOKEN_ACCOUNT_KEY, accountId);
  } catch {
    // The in-memory session can still use the token for this page load.
  }
  return expiresAt;
}

function clearStoredAccessToken() {
  try {
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(ACCESS_TOKEN_EXPIRY_KEY);
    sessionStorage.removeItem(ACCESS_TOKEN_ACCOUNT_KEY);
  } catch {
    // Session storage can be unavailable in private browsing.
  }
}

async function accessTokenMatchesAccount(accessToken: string, accountId: string): Promise<boolean> {
  const response = await fetch(
    `https://oauth2.googleapis.com/tokeninfo?access_token=${encodeURIComponent(accessToken)}`,
  );
  if (!response.ok) return false;
  const info = (await response.json()) as { sub?: string };
  return info.sub === accountId;
}

function waitForGoogle(): Promise<void> {
  return new Promise((resolve, reject) => {
    const ready = () =>
      typeof google !== "undefined" &&
      Boolean(google.accounts?.id) &&
      Boolean(google.accounts?.oauth2);

    if (ready()) {
      resolve();
      return;
    }

    const scripts = document.querySelectorAll<HTMLScriptElement>(
      'script[src^="https://accounts.google.com/gsi/client"]',
    );
    let interval: number | undefined;
    let timeout: number | undefined;
    const cleanup = () => {
      if (interval !== undefined) window.clearInterval(interval);
      if (timeout !== undefined) window.clearTimeout(timeout);
      scripts.forEach((script) => script.removeEventListener("error", onScriptError));
    };
    const finish = (error?: Error) => {
      cleanup();
      if (error) reject(error);
      else resolve();
    };
    const onScriptError = (event: Event) => {
      const script = event.currentTarget as HTMLScriptElement;
      finish(new Error(`Failed to load Google script: ${script.src}`));
    };

    scripts.forEach((script) => script.addEventListener("error", onScriptError));
    timeout = window.setTimeout(
      () => finish(new Error("Timed out waiting for Google scripts to load")),
      GOOGLE_SCRIPT_TIMEOUT_MS,
    );
    interval = window.setInterval(() => {
      if (ready()) {
        finish();
      }
    }, 50);
  });
}

export const AuthContext = createContext<AuthContextValue>({
  status: AuthStatus.INITIALIZING,
  isLoading: true,
  isAuthorized: false,
  needsDriveAccess: false,
  renderSignInButton: () => undefined,
  requestDriveAccess: () => undefined,
  signOut: () => undefined,
});

export default function AuthContextProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState(AuthStatus.INITIALIZING);
  const [googleAccessToken, setGoogleAccessToken] = useState<string>();
  const tokenClientRef = useRef<google.accounts.oauth2.TokenClient>();
  const gisReadyRef = useRef(false);
  const sessionRef = useRef<Session>({ hasJwt: restoreJWT(), ...readStoredAccessToken() });
  const authGeneration = useRef(0);
  const driveRequestRef = useRef<DriveRequestState>("idle");
  const pendingTokenRef = useRef<google.accounts.oauth2.TokenResponse | null>(null);
  const startDriveAccessRef = useRef<(hint?: string) => boolean>(() => false);
  const isLoading = status === AuthStatus.INITIALIZING;
  const isAuthorized = status === AuthStatus.AUTHORIZED;
  const needsDriveAccess = status === AuthStatus.DRIVE_ACCESS_REQUIRED;

  useEffect(() => {
    const { accessToken, expiresAt } = sessionRef.current;
    if (!googleAccessToken || !accessToken || !expiresAt) return () => {};

    const timeout = window.setTimeout(() => {
      if (
        sessionRef.current.accessToken !== accessToken ||
        sessionRef.current.expiresAt !== expiresAt
      ) {
        return;
      }

      sessionRef.current.accessToken = undefined;
      sessionRef.current.expiresAt = undefined;
      clearStoredAccessToken();
      setActiveAccessToken(undefined);
      setGoogleAccessToken(undefined);
      if (sessionRef.current.hasJwt) setStatus(AuthStatus.DRIVE_ACCESS_REQUIRED);
    }, Math.max(0, expiresAt - Date.now() - EXPIRY_BUFFER_MS));

    return () => window.clearTimeout(timeout);
  }, [googleAccessToken]);

  const publishSession = useCallback(() => {
    const { hasJwt, accessToken, expiresAt } = sessionRef.current;
    if (!hasJwt || !accessToken || !expiresAt) return;
    if (Date.now() >= expiresAt - EXPIRY_BUFFER_MS) {
      sessionRef.current.accessToken = undefined;
      sessionRef.current.expiresAt = undefined;
      clearStoredAccessToken();
      setActiveAccessToken(undefined);
      setGoogleAccessToken(undefined);
      setStatus(AuthStatus.DRIVE_ACCESS_REQUIRED);
      return;
    }

    setActiveAccessToken(accessToken, expiresAt);
    setGoogleAccessToken(accessToken);
    setStatus(AuthStatus.AUTHORIZED);
  }, []);

  const requestDriveAccess = useCallback(() => {
    if (driveRequestRef.current === "pending" || driveRequestRef.current === "committing") {
      setStatus(AuthStatus.REQUESTING_DRIVE_ACCESS);
      return;
    }

    setStatus(AuthStatus.REQUESTING_DRIVE_ACCESS);
    const hint = sessionRef.current.email ?? sessionRef.current.accountId;
    if (!startDriveAccessRef.current(hint)) {
      setStatus(AuthStatus.DRIVE_ACCESS_REQUIRED);
    }
  }, []);

  const renderSignInButton = useCallback((parent: HTMLElement) => {
    if (!gisReadyRef.current) return;
    parent.replaceChildren();
    google.accounts.id.renderButton(parent, {
      type: "standard",
      theme: "filled_blue",
      size: "large",
      shape: "rectangular",
      text: "signin_with",
      click_listener: () => {
        if (sessionRef.current.accessToken) return;
        startDriveAccessRef.current();
      },
    });
  }, []);

  const signOut = useCallback(() => {
    authGeneration.current += 1;
    sessionRef.current = { hasJwt: false };
    driveRequestRef.current = "idle";
    pendingTokenRef.current = null;
    clearStoredAccessToken();

    if (typeof google !== "undefined" && google.accounts?.id) {
      google.accounts.id.disableAutoSelect();
      google.accounts.id.cancel();
    }
    setActiveAccessToken(undefined);

    dropJWT();
    setGoogleAccessToken(undefined);
    setStatus(AuthStatus.SIGNED_OUT);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const rejectDriveAccess = () => {
      driveRequestRef.current = "failed";
      pendingTokenRef.current = null;
      if (sessionRef.current.hasJwt && !sessionRef.current.accessToken) {
        setStatus(AuthStatus.DRIVE_ACCESS_REQUIRED);
      }
    };

    const commitAccessToken = async (
      tokenResponse: google.accounts.oauth2.TokenResponse,
      accountId: string,
    ) => {
      const generation = authGeneration.current;
      const accessToken = tokenResponse.access_token;
      if (!accessToken) {
        rejectDriveAccess();
        return;
      }

      try {
        const matches = await accessTokenMatchesAccount(accessToken, accountId);
        if (cancelled || generation !== authGeneration.current) return;
        if (!matches) {
          logAuthError("Google Drive account does not match the signed-in account");
          rejectDriveAccess();
          return;
        }
      } catch (error) {
        logAuthError("Google Drive account could not be verified", error);
        if (cancelled || generation !== authGeneration.current) return;
        rejectDriveAccess();
        return;
      }

      if (cancelled || generation !== authGeneration.current || !sessionRef.current.hasJwt) return;

      const expiresIn = Number(tokenResponse.expires_in);
      const expiresAt = storeAccessToken(
        accessToken,
        Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : 3600,
        accountId,
      );
      sessionRef.current.accessToken = accessToken;
      sessionRef.current.expiresAt = expiresAt;
      driveRequestRef.current = "idle";
      pendingTokenRef.current = null;
      publishSession();
    };

    const handleToken = (tokenResponse: google.accounts.oauth2.TokenResponse) => {
      if (cancelled) return;
      if (tokenResponse.error || !tokenResponse.access_token) {
        logAuthError(
          "Google authorization failed",
          tokenResponse.error,
          tokenResponse.error_description,
        );
        rejectDriveAccess();
        return;
      }

      const granted = google.accounts.oauth2.hasGrantedAllScopes(tokenResponse, ...SCOPES);
      if (!granted) {
        logAuthError("Google authorization did not include every required scope");
        rejectDriveAccess();
        return;
      }

      const accountId = sessionRef.current.accountId;
      if (!sessionRef.current.hasJwt || !accountId) {
        pendingTokenRef.current = tokenResponse;
        driveRequestRef.current = "ready";
        return;
      }

      driveRequestRef.current = "committing";
      void commitAccessToken(tokenResponse, accountId);
    };

    const startDriveAccess = (hint?: string) => {
      if (!tokenClientRef.current) return false;
      if (driveRequestRef.current === "pending" || driveRequestRef.current === "committing") {
        return true;
      }

      driveRequestRef.current = "pending";
      pendingTokenRef.current = null;
      tokenClientRef.current.requestAccessToken({
        prompt: "",
        ...(hint ? { hint } : {}),
      });
      return true;
    };
    startDriveAccessRef.current = startDriveAccess;

    const handleCredential = async (response: google.accounts.id.CredentialResponse) => {
      if (cancelled) return;
      const generation = authGeneration.current;
      const cachedAccountId = sessionRef.current.accountId;
      const cachedAccessToken = sessionRef.current.accessToken;
      const cachedExpiresAt = sessionRef.current.expiresAt;
      setStatus(AuthStatus.SIGNING_IN);
      setActiveAccessToken(undefined);
      setGoogleAccessToken(undefined);
      try {
        const identity = jwt_decode<{ sub?: string; email?: string }>(response.credential);
        if (!identity.sub) throw new Error("Google ID token has no account identifier");
        sessionRef.current.email = identity.email;

        const canReuseAccessToken = Boolean(
          cachedAccessToken &&
          cachedAccountId === identity.sub &&
          cachedExpiresAt &&
          Date.now() < cachedExpiresAt - EXPIRY_BUFFER_MS,
        );
        const driveRequestStarted =
          driveRequestRef.current === "pending" ||
          driveRequestRef.current === "ready" ||
          driveRequestRef.current === "committing";
        if (!canReuseAccessToken && !driveRequestStarted) {
          startDriveAccess(identity.email ?? identity.sub);
        }

        const loginSucceeded = await getJWT(response.credential);
        if (cancelled || generation !== authGeneration.current) return;

        sessionRef.current.hasJwt = loginSucceeded;
        if (!loginSucceeded) {
          logAuthError("PBEEJ rejected the Google ID token");
          pendingTokenRef.current = null;
          driveRequestRef.current = "idle";
          setStatus(AuthStatus.SIGNED_OUT);
          return;
        }

        sessionRef.current.accountId = identity.sub;
        sessionRef.current.email = identity.email;
        if (canReuseAccessToken && cachedAccessToken && cachedExpiresAt) {
          sessionRef.current.accessToken = cachedAccessToken;
          sessionRef.current.expiresAt = cachedExpiresAt;
          pendingTokenRef.current = null;
          driveRequestRef.current = "idle";
          publishSession();
          return;
        }

        sessionRef.current.accessToken = undefined;
        sessionRef.current.expiresAt = undefined;
        clearStoredAccessToken();

        const pendingToken = pendingTokenRef.current;
        if (driveRequestRef.current === "ready" && pendingToken) {
          pendingTokenRef.current = null;
          driveRequestRef.current = "committing";
          await commitAccessToken(pendingToken, identity.sub);
          return;
        }

        if (driveRequestRef.current === "pending" || driveRequestRef.current === "committing") {
          setStatus(AuthStatus.REQUESTING_DRIVE_ACCESS);
          return;
        }

        setStatus(AuthStatus.REQUESTING_DRIVE_ACCESS);
        if (!startDriveAccess(identity.email ?? identity.sub)) {
          setStatus(AuthStatus.DRIVE_ACCESS_REQUIRED);
        }
      } catch (error) {
        logAuthError("Google sign-in failed", error);
        if (!cancelled && generation === authGeneration.current) {
          setStatus(AuthStatus.SIGNED_OUT);
        }
      }
    };

    const start = async () => {
      try {
        await waitForGoogle();
        if (cancelled) return;

        tokenClientRef.current = google.accounts.oauth2.initTokenClient({
          client_id: CLIENT_ID,
          scope: SCOPES.join(" "),
          callback: handleToken,
          error_callback: (error) => {
            console.warn("Google authorization popup failed", error.type);
            if (!cancelled) rejectDriveAccess();
          },
        });

        google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: (response) => {
            void handleCredential(response);
          },
          auto_select: true,
          cancel_on_tap_outside: false,
          itp_support: true,
          use_fedcm_for_button: true,
        });

        gisReadyRef.current = true;
        if (sessionRef.current.hasJwt && sessionRef.current.accessToken) {
          publishSession();
        } else if (sessionRef.current.accessToken) {
          google.accounts.id.prompt();
        }
      } catch (error) {
        logAuthError("Google sign-in setup failed", error);
      } finally {
        if (!cancelled) {
          setStatus((current) =>
            current === AuthStatus.INITIALIZING ? AuthStatus.SIGNED_OUT : current,
          );
        }
      }
    };

    void start();

    return () => {
      cancelled = true;
    };
  }, [publishSession]);

  return (
    <AuthContext.Provider
      value={{
        status,
        isLoading,
        isAuthorized,
        googleAccessToken,
        needsDriveAccess,
        renderSignInButton,
        requestDriveAccess,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
